import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { getPoles } from '../../../../../../api/theHive/redeProto';
import {
  MapMetric,
  NetworkPole,
  PoleWithPosition,
  Region,
} from '../../../../../../api/theHive/redeProto/types';
import {
  BRAZIL_CENTER,
  UF_CENTROID,
} from '../../../../../../constants/redeProto';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

const GOLDEN_ANGLE = 2.399963;
const SPREAD = 0.18;

const withPositions = (poles: NetworkPole[]): PoleWithPosition[] => {
  const perUf = new Map<string, number>();
  return poles.map((pole) => {
    // Coordenada real do backend quando disponível.
    if (pole.latitude != null && pole.longitude != null) {
      return {
        ...pole,
        position: { lat: pole.latitude, lng: pole.longitude },
      };
    }
    // Fallback: espalha ao redor do centróide da UF (cidade sem coordenada).
    const index = perUf.get(pole.uf) ?? 0;
    perUf.set(pole.uf, index + 1);
    const center = UF_CENTROID[pole.uf] ?? BRAZIL_CENTER;
    const radius = SPREAD * Math.sqrt(index);
    const angle = index * GOLDEN_ANGLE;
    return {
      ...pole,
      position: {
        lat: center.lat + radius * Math.sin(angle),
        lng: center.lng + radius * Math.cos(angle),
      },
    };
  });
};

interface UseNetworkMapHook {
  poles: PoleWithPosition[];
  filteredPoles: PoleWithPosition[];
  ufOptions: string[];
  metric: MapMetric;
  setMetric: (value: MapMetric) => void;
  region: Region | 'all';
  setRegion: (value: Region | 'all') => void;
  uf: string;
  setUf: (value: string) => void;
  selectedPole: PoleWithPosition | undefined;
  setSelectedPole: (value: PoleWithPosition | undefined) => void;
  loading: boolean;
  error: boolean;
}

export default function useNetworkMap(): UseNetworkMapHook {
  const [poles, setPoles] = useState<PoleWithPosition[]>([]);
  const [metric, setMetric] = useState<MapMetric>('coverage');
  const [region, setRegion] = useState<Region | 'all'>('all');
  const [uf, setUf] = useState('all');
  const [selectedPole, setSelectedPole] = useState<PoleWithPosition>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getPoles(selectedNoticeId);
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      setPoles(withPositions(response.data));
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const ufOptions = useMemo(() => {
    const scoped =
      region === 'all' ? poles : poles.filter((pole) => pole.region === region);
    return Array.from(new Set(scoped.map((pole) => pole.uf))).sort();
  }, [poles, region]);

  const filteredPoles = useMemo(
    () =>
      poles.filter((pole) => {
        if (region !== 'all' && pole.region !== region) return false;
        if (uf !== 'all' && pole.uf !== uf) return false;
        return true;
      }),
    [poles, region, uf]
  );

  return {
    poles,
    filteredPoles,
    ufOptions,
    metric,
    setMetric,
    region,
    setRegion,
    uf,
    setUf,
    selectedPole,
    setSelectedPole,
    loading,
    error,
  };
}
